import React, { useEffect, useState } from "react";
import performRequest from "../../../api/request";
import LoadingPage from "../../../Components/layouts/loading";
import { Toast } from "../../../CustomComponent/toast";
import Footer from "../../../Components/Footer/footer";
import { CustomErrorResponse } from "../../../CustomComponent/custom_error_response";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { FcClearFilters, FcFinePrint } from "react-icons/fc";
import DynamicFilterInput from "../../../Components/layouts/filter.inpit";
import getFilteredURL from "../../../Components/Functions/filters.getUrl";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { extractDate } from "../../../Components/Functions/date.fun";
import DynamicFilterSelect from "../../../Components/layouts/filter.select";

const MarketingPipelineSystem = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();
  const [socialMedia, setSocialMedia] = useState([]);
  const [pageMap, setPageMap] = useState({});
  const ITEMS_PER_PAGE = 20;

  const [filters, setFilters] = useState({
    startdate: "",
    enddate: "",
    task: "",
    videoEditor: "",
    brand: "",
  });

  // ✅ YOUR MARKETING STATUSES (change if needed)
  const statuses = [
    "New Task",
    "First Draft",
    "Story & Fine Cut",
    "GraphicsText & Sound",
    "Color Grading & VFX",
    "Export",
    "Published",
  ];

  // ✅ FETCH DATA
  // useEffect(() => {
  //   setLoading(true);
  //   performRequest("GET", getFilteredURL("/content/get-pipelines", 1, filters))
  //     .then((res) => {
  //       if (res.status === 200) {
  //         setData(res.data);
  //       }
  //     })
  //     .catch((err) => CustomErrorResponse(err))
  //     .finally(() => setLoading(false));
  // }, [refresh]);

  useEffect(() => {
    setLoading(true);
    performRequest(
      "GET",
      getFilteredURL("/content/get-pipelines", 1, {
        ...filters,
        pageMap: JSON.stringify(pageMap),
        limit: ITEMS_PER_PAGE,
      })
    )
      .then((res) => {
        if (res.status === 200) {
          setData(res.data);
        }
      })
      .catch((err) => CustomErrorResponse(err))
      .finally(() => setLoading(false));
  }, [refresh, pageMap]);

  useEffect(() => {
    setLoading(true);
    performRequest("GET", `/podcast/social-media-users`)
      .then((res) => {
        if (res.status === 200) {
          setSocialMedia(res.data);
          // console.log(buyersManagementInfo);
        } else {
          Toast("error", "Error while getting social media!");
        }
        setLoading(false);
      })
      .catch((err) => {
        // Toast("error", "Server Error!");
        // console.error(err);
        CustomErrorResponse(err);
      })
      .finally(() => setLoading(false));
  }, []);

  // ✅ GET ITEMS BY STATUS
  // const getItemsByStatus = (status) => {
  //   return data?.[status] || [];
  // };

  const getItemsByStatus = (status) => {
    const statusData = data?.[status] || {};

    return {
      paginatedItems: statusData.data || [],
      totalItems: statusData.total || 0,
      currentPage: statusData.page || 1,
      totalPages: statusData.totalPages || 1,
    };
  };

  // ✅ DRAG END HANDLER
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    const sourceStatus = source.droppableId;
    const destStatus = destination.droppableId;

    if (sourceStatus === destStatus) return;

    try {
      let res = await performRequest(
        "PATCH",
        `/content/update/${draggableId}`,
        {
          marketing_status: destStatus,
        }
      );

      if (res.status === 200) {
        // Toast("success", "Moved successfully");
      }

      setRefresh((prev) => !prev);
    } catch (err) {
      CustomErrorResponse(err);
    }
  };

  const handleNext = (status) => {
    setPageMap((prev) => ({
      ...prev,
      [status]: (prev[status] || 1) + 1,
    }));
  };

  const handlePrev = (status) => {
    setPageMap((prev) => ({
      ...prev,
      [status]: Math.max((prev[status] || 1) - 1, 1),
    }));
  };

  const statusColors = {
    "New Task": "bg-blue-lt",
    "First Draft": "bg-purple-lt", // blue
    "Story & Fine Cut": "bg-yellow-lt", // yellow
    "GraphicsText & Sound": "bg-teal-lt ", // teal
    "Color Grading & VFX": "bg-orange-lt", // purple
    Export: "bg-cyan-lt", // orange
    Published: "bg-green-lt", // green
  };

  const statusScripts = {
    "New Task": [
      "Asset Collection, Verify B-Roll, Gather Audio File, Get script, Branding Assets",
    ],
    "First Draft": [
      "Goal: Build skeleton, not perfection. Place clips in sequence, Remove dead air, trimming, Sync audio + video, No effects, no music, no polish.",
    ],
    "Story & Fine Cut": [
      "Goal: Make it engaging. Improve pacing, Add jump cuts, Frame-level trimming, Word level zooming,  Remove boring parts, Add first 5 sec hook.",
    ],
    "GraphicsText & Sound": [
      "Add Key text highlights, Insert lower thirds (names, title), Add CTA overlays, Background music, Sound effects (whoosh, click, impact)",
    ],
    "Color Grading & VFX": [
      "Apply LUTs, Fix exposure, Match camera colors, Skin tone correction, Motion graphics, Transitions",
    ],
    Export: [
      "Hook Strong, Backgound music added, Audio Clean, Spelling check for every word, Branding Consistent",
    ],
    Published: ["Thumbnail, Title, Description, Hashtags"],
  };

  if (loading) return <LoadingPage />;

  const handleView = (item) => {
    console.log("item is", item);
    switch (item.type) {
      case "podcast":
        navigate(`/podcasts/view/${item?.ref_id}`);
        break;

      case "graphics":
        navigate(`/graphics-view/${item?.ref_id}`);
        break;

      case "reel":
        navigate(`/reel-shorts/${item?.ref_id}`);
        break;

      case "course":
        navigate(`/course-view/${item?.ref_id}`);
        break;

      case "informative video":
        navigate(`/informative-video/${item?.ref_id}`);
        break;

      default:
        navigate(`/`);
    }
  };

  const tagFullForm = {
    FD: "First Draft",
    TH: "Thumbnail",
    CS: "Coming Soon Poster",
  };

  const getFullForm = (tag) => {
    if (!tag) return "N/A";

    const tagsArray = Array.isArray(tag) ? tag : tag.split(",");

    return tagsArray
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean)
      .map((t) => tagFullForm[t] || t)
      .join(", ");
  };

  return (
    <div className="page p-4">
      <div className="page-wrapper">
        <div className="card shadow-sm p-3">
          <div className="d-flex justify-content-between align-items-center">
            {/* ✅ LEFT SIDE - HEADING */}
            <h2 className="fw-bold mb-0">
              <FcFinePrint
                style={{ height: "30px", width: "30px" }}
              ></FcFinePrint>
              Content Pipeline
            </h2>

            <div className="d-flex gap-3 align-items-center flex-wrap justify-content-center">
              {/* FD */}
              <div className="px-2 py-1 border rounded">
                <small className="fs-5 ms-1">First Draft Url : </small>
                <strong>FD </strong>
              </div>

              {/* CS */}
              <div className="px-2 py-1 border rounded">
                <small className="fs-5 ms-1">Coming Soon Poster : </small>
                <strong>CS</strong>
              </div>

              {/* TH */}
              <div className="px-2 py-1 border rounded">
                <small className="fs-5 ms-1 ms-1">Thumbnail : </small>
                <strong>TH</strong>
              </div>

              {/* URL */}
              <div className="px-2 py-1 border rounded bg-green-lt">
                <small className="ms-1 fs-5">Final URL</small>
              </div>

              {/* PUB */}
              <div className="px-2 py-1 border rounded bg-orange-lt">
                <small className="ms-1 fs-5">Published</small>
              </div>

              {/* BOTH */}
              <div className="px-2 py-1 border rounded bg-purple-lt">
                <small className="ms-1 fs-5">Final url + Published</small>
              </div>
            </div>

            {/* ✅ RIGHT SIDE - FILTER BUTTON */}
            <div className="dropdown">
              <button
                className="btn btn-primary d-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <FcClearFilters size={18} />
                <span className="ms-1">Apply Filters</span>
              </button>

              <div
                className="dropdown-menu dropdown-menu-end p-3"
                style={{ minWidth: "250px" }}
              >
                <DynamicFilterInput
                  label="Start date"
                  value={filters.startdate}
                  onChange={(e) =>
                    setFilters({ ...filters, startdate: e.target.value })
                  }
                  type="date"
                />

                <DynamicFilterInput
                  label="End date"
                  value={filters.enddate}
                  onChange={(e) =>
                    setFilters({ ...filters, enddate: e.target.value })
                  }
                  type="date"
                />

                <DynamicFilterInput
                  label="Task"
                  value={filters.task}
                  onChange={(e) =>
                    setFilters({ ...filters, task: e.target.value })
                  }
                  type="text"
                  placeholder="Search Task"
                />

                {/* <DynamicFilterInput
                  label="Brand"
                  value={filters.brand}
                  onChange={(e) =>
                    setFilters({ ...filters, brand: e.target.value })
                  }
                  type="text"
                  placeholder="Search Brand"
                /> */}
                <div className="mb-2">
                  <label>Brand</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filters.brand}
                    onChange={(e) =>
                      setFilters({ ...filters, brand: e.target.value })
                    }
                    placeholder="Search Brand"
                  />
                </div>

                <div className="col-12">
                  <DynamicFilterSelect
                    label="Select Editor Name:"
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        videoEditor: e.target.value,
                      })
                    }
                    value={filters.videoEditor}
                  >
                    {/* Default option */}
                    <option value="">Select</option>
                    {socialMedia?.map((item, index) => {
                      return (
                        <option key={index} value={item?.userName}>
                          {item?.userName}
                        </option>
                      );
                    })}
                  </DynamicFilterSelect>
                </div>

                <div className="d-flex gap-2 mt-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={() => setRefresh(!refresh)}
                  >
                    Search
                  </button>

                  <button
                    className="btn btn-secondary w-100"
                    onClick={() => {
                      setFilters({
                        startdate: "",
                        enddate: "",
                        task: "",
                        videoEditor: "",
                        brand: "",
                      });
                      setRefresh(!refresh);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body mt-2">
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="card">
              <div
                className="card-body"
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: "1rem",
                  minHeight: "80vh",
                }}
              >
                {statuses.map((status) => {
                  // const items = getItemsByStatus(status);

                  const {
                    paginatedItems,
                    totalItems,
                    currentPage,
                    totalPages,
                  } = getItemsByStatus(status);

                  return (
                    <Droppable droppableId={status} key={status}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          className="border"
                          {...provided.droppableProps}
                          style={{
                            minWidth: "280px",
                            background: "#f8f9fa",
                            borderRadius: "8px",
                            padding: "10px",
                            maxHeight: "80vh",
                            overflowY: "auto",
                          }}
                        >
                          <h3
                            style={{
                              textAlign: "center",
                              background: "#000",
                              color: "#fff",
                              padding: "8px",
                              borderRadius: "6px",
                              textTransform: "uppercase",
                            }}
                          >
                            {/* {status} ({items.length}) */}
                            {status} ({totalItems})
                          </h3>
                          <div
                            className="border mb-2 p-2 bg-gray-500 text-gray-500-fg"
                            style={{ height: "100px" }}
                          >
                            <p className="fs-5" style={{ fontWeight: "bold" }}>
                              {statusScripts[status]}
                            </p>
                          </div>
                          {paginatedItems.map((item, index) => (
                            <Draggable
                              key={item.id}
                              draggableId={item.id.toString()}
                              index={index} // ✅ FIXED
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  // className={`card mb-2 p-2 ${
                                  //   item?.finalPhotos === "green"
                                  //     ? "bg-green-lt"
                                  //     : statusColors[status]
                                  // }`}
                                  className={`card mb-2 p-2 ${
                                    item?.isBothPublish === "purple"
                                      ? "bg-purple-lt"
                                      : item?.finalPhotos === "green"
                                      ? "bg-green-lt"
                                      : item?.isPublish === "orenge"
                                      ? "bg-orange-lt"
                                      : "bg-blue-lt"
                                  }`}
                                >
                                  <div className="mt-1 ">
                                    <strong className="fs-3">
                                      Task :{" "}
                                      <b>{item?.contentCardId || "N/A"}</b>
                                    </strong>{" "}
                                  </div>
                                  <hr className="mt-2 mb-1"></hr>

                                  <div className="mt-1 ">
                                    <strong className="fs-4">
                                      Editor : {item?.assignedEditor || "N/A"}
                                    </strong>{" "}
                                  </div>
                                  <div className="mt-1">
                                    <span className="">
                                      <strong className="fs-4">
                                        Assigned :{" "}
                                        {extractDate(item?.createdAt || "N/A")}
                                      </strong>{" "}
                                    </span>
                                  </div>
                                  <div className="mt-1">
                                    <span className="">
                                      <strong className="fs-4">
                                        Deadline :{" "}
                                        {extractDate(
                                          item?.taskdedline || "N/A"
                                        )}
                                      </strong>{" "}
                                    </span>
                                  </div>

                                  <div className="mt-1 ">
                                    <strong className="fs-4">
                                      Brand : {item?.brandAsset || "N/A"}
                                    </strong>{" "}
                                  </div>

                                  {item?.tag && (
                                    <div className="mt-1 mb-2">
                                      <div className="tooltip-container">
                                        <strong className="badge bg-yellow-lt text-yellow fs-4">
                                          {item?.tag}
                                        </strong>

                                        <span className="custom-tooltip">
                                          {getFullForm(item?.tag)}
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  <hr className="m-0 p-0"></hr>
                                  <div className="mt-2 ">
                                    <button
                                      className="btn btn-sm btn-light border"
                                      onClick={() => handleView(item)}
                                      title="View"
                                    >
                                      <FaEye />
                                      &nbsp;view
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}

                          {provided.placeholder}
                          {/* ✅ PAGINATION BUTTONS */}
                          <hr className="m-0 p-0"></hr>
                          {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              {/* PREV BUTTON */}
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage === 1}
                                onClick={() => handlePrev(status)}
                              >
                                Prev
                              </button>

                              {/* PAGE INFO */}
                              <small >
                               Page {currentPage} / {totalPages}
                              </small>

                              {/* NEXT BUTTON */}
                              <button
                                className="btn btn-sm btn-outline-primary"
                                disabled={currentPage === totalPages}
                                onClick={() => handleNext(status)}
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </div>
          </DragDropContext>
        </div>

        <Footer />
      </div>
      <style>{`
        .tooltip-container {
  position: relative;
  display: inline-block;
}

.custom-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 125%; /* always above */
  left: 50%;
  transform: translateX(-50%);
  background-color: #1d273b;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  white-space: normal;
  max-width: 200px;
  transition: all 0.2s ease;
  z-index: 9999;
}

.tooltip-container:hover .custom-tooltip {
  visibility: visible;
  opacity: 1;
  transform: translateX(-50%) translateY(-4px);
}

.custom-tooltip::after {
  content: "";
  position: absolute;
  top: 100%; /* arrow pointing down */
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: #1d273b transparent transparent transparent;
}
      `}</style>
    </div>
  );
};

export default MarketingPipelineSystem;
